import axios from 'axios';
import { API_BASE_URL } from './session';

// Documents of the service's central Dropbox, always reached through the server (rights
// checked there). Staff never see a Dropbox link.
const BASE = `${API_BASE_URL}/api/documents`;

let status = null;
// { ready, maxSize, pieceSize }: ready is false while the server is not linked to the Dropbox account.
export const documentsStatus = () => {
  if (!status) {
    status = axios
      .get(`${BASE}/status`)
      .then((response) => response.data)
      .catch(() => {
        status = null;
        return { ready: false, maxSize: 0 };
      });
  }
  return status;
};

const progress = (onProgress) => (event) => {
  if (onProgress && event.total) onProgress(event.loaded / event.total);
};

const fileForm = (file, fields = {}) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  form.append('name', file.name);
  form.append('file', file);
  return form;
};

// Up to this size a file goes in one request; above, in pieces that the server forwards one by
// one to the service's Dropbox (large imaging files, videos...).
const DIRECT_MAX = 8 * 1024 * 1024;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// start: { section, patientId, unit } for a new document, { replaces: id } for a new version.
// A piece lost on the way is sent again (a few times) from where the server stands.
const sendInPieces = async (file, start, onProgress) => {
  const { data } = await axios.post(`${BASE}/uploads`, { ...start, name: file.name, size: file.size, type: file.type || '' });
  let offset = 0;
  let failures = 0;
  while (offset < file.size) {
    const from = offset;
    const piece = file.slice(from, Math.min(from + data.pieceSize, file.size));
    try {
      // eslint-disable-next-line no-await-in-loop
      const answer = await axios.put(`${BASE}/uploads/${data.id}`, piece, {
        headers: { 'Content-Type': 'application/octet-stream', 'X-Upload-Offset': String(from) },
        onUploadProgress: (event) => onProgress && onProgress(Math.min(1, (from + (event.loaded || 0)) / file.size)),
      });
      offset = answer.data.received;
      failures = 0;
    } catch (error) {
      const response = error.response;
      const resume = response && response.status === 409 && response.data && typeof response.data.received === 'number';
      if ((!resume && response && response.status < 500) || failures >= 4) throw error;
      failures += 1;
      if (resume) offset = response.data.received;
      // eslint-disable-next-line no-await-in-loop
      await wait(1000 * failures);
    }
  }
  const done = await axios.post(`${BASE}/uploads/${data.id}/finish`);
  if (onProgress) onProgress(1);
  return done.data;
};

export const uploadDocument = (file, fields, onProgress) =>
  file.size > DIRECT_MAX
    ? sendInPieces(file, fields, onProgress)
    : axios.post(BASE, fileForm(file, fields), { onUploadProgress: progress(onProgress) }).then((response) => response.data);

export const replaceDocument = (id, file, onProgress) =>
  file.size > DIRECT_MAX
    ? sendInPieces(file, { replaces: id }, onProgress)
    : axios.put(`${BASE}/${id}`, fileForm(file), { onUploadProgress: progress(onProgress) }).then((response) => response.data);

export const archiveDocument = (id) => axios.delete(`${BASE}/${id}`);

// Documents listed in a record: [{ id, name, mimeType }].
export const storedDocuments = (list) => (Array.isArray(list) ? list.filter((item) => item && item.id) : []);

// Sends the files chosen on the device one after the other, e.g. fields = { section: 'cro',
// patientId, unit }. onProgress({ index, ratio }) follows the file being sent. On failure,
// error.sent lists the files already in the service's Dropbox.
export const sendFiles = async (files, fields, onProgress) => {
  const sent = [];
  for (let index = 0; index < files.length; index += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const saved = await uploadDocument(files[index], fields, (ratio) => onProgress && onProgress({ index, ratio }));
      sent.push({ id: saved.id, name: saved.name, mimeType: saved.mimeType });
    } catch (error) {
      error.sent = sent;
      throw error;
    }
  }
  return sent;
};

// Removed documents go to the archives of the service's Dropbox. Returns those that failed.
export const archiveDocuments = async (items) => {
  const failed = [];
  for (const item of items) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await archiveDocument(item.id);
    } catch (error) {
      if (!(error.response && error.response.status === 404)) failed.push(item);
    }
  }
  return failed;
};

// Recovery of a former Dropbox link (administration): { document, reused }.
export const importLink = (fields) => axios.post(`${BASE}/import`, fields).then((response) => response.data);

export const reportImport = (counts) => axios.post(`${BASE}/import/report`, counts).catch(() => {});

export const fetchDocument = (id) => axios.get(`${BASE}/${id}`).then((response) => response.data);

// The file (or its conversion, or an earlier version) as a Blob kept in the page.
export const fetchContent = (id, { preview = false, version = null, download = false, onProgress, signal } = {}) => {
  const path = version !== null ? `versions/${version}/content` : preview ? 'preview' : 'content';
  return axios
    .get(`${BASE}/${id}/${path}${download ? '?download=1' : ''}`, { responseType: 'blob', onDownloadProgress: progress(onProgress), signal })
    .then((response) => response.data);
};

// Thumbnail of a photo as an object URL, kept for the session (galleries show dozens).
// Resolves to '' when the file has no thumbnail.
const thumbnails = new Map();
export const thumbnailUrl = (id) => {
  if (!thumbnails.has(id)) {
    const loading = axios
      .get(`${BASE}/${id}/thumbnail`, { responseType: 'blob' })
      .then((response) => URL.createObjectURL(response.data))
      .catch((error) => {
        // Retried later unless the file simply has no thumbnail.
        if (!(error.response && error.response.status === 415)) thumbnails.delete(id);
        return '';
      });
    thumbnails.set(id, loading);
  }
  return thumbnails.get(id);
};

export const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// Error sent by the server as a JSON Blob (files are requested as Blobs).
export const documentError = async (error, fallback) => {
  const data = error && error.response && error.response.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).message || fallback;
    } catch (parseError) {
      return fallback;
    }
  }
  return (data && data.message) || fallback;
};

const EXTENSIONS = {
  pdf: ['pdf'],
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif'],
  video: ['mp4', 'webm', 'mov', 'm4v', 'ogv'],
  audio: ['mp3', 'wav', 'm4a', 'ogg', 'aac'],
  text: ['txt'],
  // Converted by Dropbox: to PDF (documents, slides) or to a table (spreadsheets).
  office: ['doc', 'docx', 'docm', 'odt', 'rtf', 'ppt', 'pptx', 'pptm', 'pps', 'ppsx', 'ppsm', 'odp'],
  sheet: ['xls', 'xlsx', 'xlsm', 'ods', 'csv'],
};

export const extensionOf = (name = '') => {
  const match = /\.([a-z0-9]+)$/i.exec(name);
  return match ? match[1].toLowerCase() : '';
};

export const kindOf = (name, mimeType = '') => {
  const extension = extensionOf(name);
  const found = Object.keys(EXTENSIONS).find((kind) => EXTENSIONS[kind].includes(extension));
  if (found) return found;
  if (mimeType === 'application/pdf') return 'pdf';
  if (/^image\//.test(mimeType)) return 'image';
  if (/^video\//.test(mimeType)) return 'video';
  if (/^audio\//.test(mimeType)) return 'audio';
  return 'other';
};

export const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  if (bytes >= 1024 * 1024 * 1024) return `${String(Math.round((bytes / (1024 * 1024 * 1024)) * 10) / 10).replace('.', ',')} Go`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0).replace('.', ',')} Mo`;
};
